import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useJyotishaLanguage } from '../components/jyotisha/JyotishaLanguageContext';
import { useAuth } from './AuthContext';
import {
  startClapEventPolling,
  subscribeClapActivityBroadcast,
  subscribePageClapEvents,
} from '../lib/supabase/pageClapRealtime';
import { formatPageClapToastMessage } from '../utils/pageClapToastCopy';
import { ClapHandsImage } from '../components/feedback/ClapHandsImage';

const ClapActivityToastCtx = createContext(null);

const TOAST_DISMISS_MS = 10000;
const HINT_TOAST_DISMISS_MS = 2000;
const MAX_QUEUE = 5;
const TOAST_DEDUPE_MS = 1200;

const CLAP_TOAST_FIREWORKS = [
  { left: '-6%', top: '18%', delay: '0ms', color: '#fbbf24' },
  { left: '96%', top: '12%', delay: '90ms', color: '#f472b6' },
  { left: '48%', top: '-28%', delay: '180ms', color: '#22d3ee' },
  { left: '10%', top: '78%', delay: '140ms', color: '#a78bfa' },
  { left: '90%', top: '72%', delay: '260ms', color: '#fb7185' },
  { left: '28%', top: '-18%', delay: '320ms', color: '#facc15' },
];

/**
 * @typedef {{ id: string; message: string; variant?: 'activity' | 'hint' }} ClapToastItem
 */

/**
 * Global clap toasts - any page, signed-in or anonymous.
 * Uses Supabase Broadcast (not postgres_changes) so all connected browsers receive activity.
 * @param {{ children: React.ReactNode; isDark: boolean }} props
 */
export function ClapActivityToastProvider({ children, isDark }) {
  const { showEnglish } = useJyotishaLanguage();
  const { user } = useAuth();
  const [activeToast, setActiveToast] = useState(/** @type {ClapToastItem | null} */ (null));
  const pendingRef = useRef(/** @type {ClapToastItem[]} */ ([]));
  const showingRef = useRef(false);
  const dismissTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const showEnglishRef = useRef(showEnglish);
  const userIdRef = useRef(user?.id ?? null);
  const lastToastAtByPageRef = useRef(/** @type {Map<string, number>} */ (new Map()));
  const seenEventIdsRef = useRef(/** @type {Set<string>} */ (new Set()));
  showEnglishRef.current = showEnglish;
  userIdRef.current = user?.id ?? null;

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const showToast = useCallback(
    (/** @type {ClapToastItem} */ item, dismissMs = TOAST_DISMISS_MS) => {
      clearDismissTimer();
      showingRef.current = true;
      setActiveToast(item);
      dismissTimerRef.current = window.setTimeout(() => {
        showingRef.current = false;
        setActiveToast(null);
      }, dismissMs);
    },
    [clearDismissTimer],
  );

  const dequeueNext = useCallback(() => {
    if (showingRef.current || pendingRef.current.length === 0) return;
    const [next, ...rest] = pendingRef.current;
    pendingRef.current = rest;
    showToast(next);
  }, [showToast]);

  const enqueueToast = useCallback(
    (/** @type {{ id?: string; pageKey: string; pageTitle?: string | null; actorUserId?: string | null; pageClapTotal: number }} */ event) => {
      if (!event.pageKey) return;

      const actorUserId = event.actorUserId ?? null;
      const currentUserId = userIdRef.current;
      if (actorUserId && currentUserId && actorUserId === currentUserId) return;

      if (event.id) {
        if (seenEventIdsRef.current.has(event.id)) return;
        seenEventIdsRef.current.add(event.id);
        if (seenEventIdsRef.current.size > 300) {
          const keep = new Set(Array.from(seenEventIdsRef.current).slice(-150));
          seenEventIdsRef.current = keep;
        }
      }

      const now = Date.now();
      const lastAt = lastToastAtByPageRef.current.get(event.pageKey) ?? 0;
      if (now - lastAt < TOAST_DEDUPE_MS) return;
      lastToastAtByPageRef.current.set(event.pageKey, now);

      const message = formatPageClapToastMessage({
        pageTitle: event.pageTitle,
        pageKey: event.pageKey,
        pageClapTotal: event.pageClapTotal,
        showEnglish: showEnglishRef.current,
      });
      const item = { id: `${now}-${Math.random().toString(36).slice(2)}`, message, variant: 'activity' };

      if (!showingRef.current && pendingRef.current.length === 0) {
        showToast(item);
        return;
      }

      pendingRef.current = [...pendingRef.current, item].slice(-MAX_QUEUE);
    },
    [showToast],
  );

  const showClapHintToast = useCallback(
    (/** @type {string} */ message) => {
      if (!message) return;
      pendingRef.current = [];
      const item = {
        id: `hint-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        message,
        variant: 'hint',
      };
      showToast(item, HINT_TOAST_DISMISS_MS);
    },
    [showToast],
  );

  const enqueueToastRef = useRef(enqueueToast);
  enqueueToastRef.current = enqueueToast;

  useEffect(() => {
    if (activeToast !== null) return undefined;
    dequeueNext();
    return undefined;
  }, [activeToast, dequeueNext]);

  useEffect(() => {
    const pushEvent = (/** @type {{ id?: string; pageKey: string; pageTitle?: string | null; actorUserId?: string | null; pageClapTotal: number }} */ event) => {
      enqueueToastRef.current({
        id: event.id,
        pageKey: event.pageKey,
        pageTitle: event.pageTitle,
        actorUserId: event.actorUserId,
        pageClapTotal: event.pageClapTotal,
      });
    };

    const unsubscribeBroadcast = subscribeClapActivityBroadcast(pushEvent);
    const unsubscribeEvents = subscribePageClapEvents(pushEvent);
    const stopPolling = startClapEventPolling(pushEvent);

    return () => {
      unsubscribeBroadcast();
      unsubscribeEvents();
      stopPolling();
    };
  }, []);

  useEffect(() => () => clearDismissTimer(), [clearDismissTimer]);

  const value = useMemo(
    () => ({ pushClapToast: enqueueToast, showClapHintToast }),
    [enqueueToast, showClapHintToast],
  );

  const isHintToast = activeToast?.variant === 'hint';

  const toastLayer =
    activeToast && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`clap-toast-anchor pointer-events-none fixed z-[48] flex justify-end inset-x-3 sm:inset-x-auto sm:left-auto sm:right-[max(0.75rem,env(safe-area-inset-right))] sm:w-[min(calc(100vw-1.5rem),17.5rem)] ${
              user ? 'clap-toast-anchor--mobile-tab' : ''
            }`}
          >
            <div
              className={`clap-toast-root relative w-full ${isHintToast ? 'max-w-[13rem]' : 'max-w-[17.5rem]'}`}
              role="status"
              aria-live="polite"
            >
              {!isHintToast
                ? CLAP_TOAST_FIREWORKS.map((burst) => (
                    <span
                      key={`${burst.left}-${burst.top}-${burst.delay}`}
                      className="clap-toast-firework pointer-events-none absolute"
                      style={{ left: burst.left, top: burst.top, animationDelay: burst.delay, color: burst.color }}
                      aria-hidden
                    />
                  ))
                : null}
              <div
                className={`clap-toast-card relative flex items-center justify-center gap-2 rounded-xl text-center leading-snug ${
                  isHintToast
                    ? `clap-toast-card--hint border-0 px-2.5 py-1.5 text-[10px] font-medium sm:px-3 sm:py-2 ${
                        isDark
                          ? 'clap-toast-card--hint-dark text-amber-100/90'
                          : 'clap-toast-card--hint-light text-amber-900/90'
                      }`
                    : `px-3 py-2.5 text-xs font-semibold sm:px-4 sm:py-3 sm:text-sm ${
                        isDark
                          ? 'clap-toast-card--dark border-amber-400/40 bg-amber-950/95 text-amber-100'
                          : 'clap-toast-card--light border-amber-300 bg-amber-50 text-amber-900'
                      }`
                } ${showEnglish ? '' : 'font-devanagari'}`}
                lang={showEnglish ? 'en' : 'hi'}
              >
                {!isHintToast ? (
                  <span
                    className={`clap-toast-glow ${isDark ? 'clap-toast-glow--dark' : 'clap-toast-glow--light'}`}
                    aria-hidden
                  />
                ) : null}
                <ClapHandsImage
                  active
                  size={isHintToast ? 'lg' : 'sm'}
                  className={`relative z-[1] shrink-0 ${isHintToast ? 'clap-toast-icon--hint' : 'clap-toast-icon'}`}
                />
                <span className="relative z-[1] min-w-0">{activeToast.message}</span>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <ClapActivityToastCtx.Provider value={value}>
      {children}
      {toastLayer}
    </ClapActivityToastCtx.Provider>
  );
}

/** @returns {{ pushClapToast: (event: object) => void; showClapHintToast: (message: string) => void } | null} */
export function useClapActivityToastOptional() {
  return useContext(ClapActivityToastCtx);
}
